import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppInit } from '@us/web/shared/app';
import { LocalStorageService } from '@us/web/shared/data-access';
import { of, switchMap, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as ShopActions from './shop.actions';
import { ShopService } from './shop.service';
import { selectCurrentLocation } from '@us/web/position/data-access';

@Injectable()
export class ShopEffects {
    loadShop$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppInit, ShopActions.loadShop),
            concatLatestFrom(() => this.store.select(selectCurrentLocation)),
            switchMap(([{ shopId }, location]) =>
                this.shopService.getShopInfo({ shop_id: shopId, lat: location?.lat, lng: location?.lng }).pipe(
                    map(response => ShopActions.loadShopSuccess({ shop: response })),
                    catchError(error => of(ShopActions.loadShopFailure(error)))
                )
            )
        );
    });

    loadShopSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(ShopActions.loadShopSuccess),
                tap(({ shop }) => {
                    console.log('localStorage.setItem shop_id: ' + shop.id);
                    this.localStorage.setItem('shop_id', shop.id);

                    const { name, description, logo } = shop;
                    this.title.setTitle(name);

                    if (logo) {
                        const favIcon: HTMLLinkElement | null = document.querySelector('#appIcon');
                        if (favIcon !== null) {
                            favIcon.href = logo;
                        }
                    }

                    this.metaTagService.addTags([
                        { name: 'robots', content: 'index, follow' },
                        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
                        { charset: 'UTF-8' },
                        { property: 'og:type', content: 'website' },
                        { property: 'og:title', content: name },
                        { property: 'og:description', content: description },
                        logo ? { property: 'og:image', content: logo } : {}
                    ]);
                })
            );
        },
        { dispatch: false }
    );

    loadShopFailure$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(ShopActions.loadShopFailure),
                map(async () => {
                    await this.router.navigateByUrl('/unavailable');
                })
            );
        },
        { dispatch: false }
    );

    constructor(
        private readonly actions$: Actions,
        private readonly shopService: ShopService,
        private readonly router: Router,
        private readonly localStorage: LocalStorageService,
        private readonly metaTagService: Meta,
        private readonly title: Title,
        private readonly store: Store
    ) {}
}
